import React, { useState, useEffect } from 'react';
import { Form, FormHistory, formService } from '../../../services/formService';
import { useAuth } from '../../../contexts/AuthContext';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { Alert, Button, Form as UiForm, HistoryPanel, ListGroup, Spinner, StatusBadge } from '@/components/ui';

const FORM_HISTORY_STAGE_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  updated: 'Updated',
  corrected: 'Resubmitted after revisions',
  rejected: 'Sent back for revisions',
  approved: 'Approved',
  're-approved': 'Re-approved',
  declined: 'Declined',
  decline_withdrawn: 'Decline withdrawn',
  approval_withdrawn: 'Approval withdrawn',
  revisions_withdrawn: 'Revision request withdrawn',
  comment: 'Comment',
  // HE-F21. Covers both answers - the comment says which, and why if a send failed.
  outbound_email: 'Vendor PDF email',
};

function formHistoryStageLabel(stage: string): string {
  return FORM_HISTORY_STAGE_LABELS[stage] || `Form ${stage}`;
}

interface FormHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  form: Form;
  onAddComment: (updatedForm: Form) => void;
}

const FormHistoryModal: React.FC<FormHistoryModalProps> = ({
  visible,
  onClose,
  form,
  onAddComment
}) => {
  const { currentUser } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localHistory, setLocalHistory] = useState<FormHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch history when modal becomes visible
  useEffect(() => {
    const fetchHistory = async () => {
      if (visible) {
        try {
          setIsLoading(true);
          const history = await formService.getFormHistory(form.id);
          setLocalHistory(history);
        } catch (error) {
          console.error('Error fetching form history:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchHistory();
  }, [visible, form.id]);

  const applySoftReload = (refetchedHistory: FormHistory[]) => {
    setLocalHistory(refetchedHistory);
    setNewComment('');
    onAddComment({ ...form, formHistory: refetchedHistory });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const commentText = newComment.trim();
    if (!commentText || !currentUser?.id) return;

    setIsSubmitting(true);
    const previousLength = localHistory.length;

    try {
      await formService.addHistoryComment(form.id, {
        comments: commentText,
        userId: currentUser.id,
        isExternal: false
      });

      // Soft reload: refetch history and update modal (API returns single entry, not full form)
      const refetchedHistory = await formService.getFormHistory(form.id);
      applySoftReload(refetchedHistory);
    } catch (error) {
      console.error('Error adding comment:', error);
      // Recovery: entry may have been saved but response failed (e.g. serialization)
      try {
        const refetchedHistory = await formService.getFormHistory(form.id);
        const hasNewEntry = refetchedHistory.length > previousLength ||
          refetchedHistory.some((h) => h.comments === commentText && h.stage === 'comment');
        if (hasNewEntry) {
          applySoftReload(refetchedHistory);
          return;
        }
      } catch (_) {
        // Refetch failed; fall through to alert
      }
      alert('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sortedHistory = localHistory.sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title="Form History"
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <HistoryPanel
        composer={
          <form onSubmit={handleSubmit}>
            <div className="tw-mb-3">
              <UiForm.Label>Comment</UiForm.Label>
              <UiForm.Control
                as="textarea"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                // Shorter on a phone, where the composer sits above the timeline and a
                // six-row textarea would push the history off screen on its own.
                rows={3}
                placeholder="Enter your comment here..."
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              className="tw-w-full"
              disabled={!newComment.trim() || isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Comment'}
            </Button>
          </form>
        }
      >
        {isLoading ? (
          <div className="tw-text-center">
            <Spinner className="tw-text-primary">Loading...</Spinner>
          </div>
        ) : sortedHistory && sortedHistory.length > 0 ? (
          <ListGroup>
            {sortedHistory.map((history) => (
              <ListGroup.Item key={history.id}>
                <div className="tw-flex tw-justify-between tw-items-start">
                  <div className="tw-min-w-0">
                    <h6 className="tw-mb-1">
                      {history.field ? (
                        <>
                          Field <strong>{history.field}</strong> changed from "{history.oldValue}" to "{history.newValue}"
                        </>
                      ) : (
                        formHistoryStageLabel(history.stage)
                      )}
                    </h6>
                    <p className="tw-mb-1 tw-text-content-muted">
                      Stage: <StatusBadge status={history.stage} label={formHistoryStageLabel(history.stage)} />
                    </p>
                    {history.comments && (
                      <p className="tw-mb-1">{history.comments}</p>
                    )}
                    <small className="tw-text-content-muted">
                      {history.user ? (
                        `By ${history.user.name} ${history.user.lastName}`
                      ) : (
                        'By System'
                      )}
                    </small>
                  </div>
                  <small className="tw-shrink-0 tw-text-content-muted tw-ml-2">
                    {new Date(history.createdAt).toLocaleString()}
                  </small>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <Alert variant="info">No history records found.</Alert>
        )}
      </HistoryPanel>
    </ResponsiveModal>
  );
};

export default FormHistoryModal; 